import { useState, useEffect } from 'react';
import { io, Socket } from 'socket.io-client';
import { useDispatch } from 'react-redux';
import doneNotify from 'src/notifications/doneNotify';
import { updateOrderStatus } from 'src/redux/reducers/ordersReduser';
import { IOrder } from 'src/function/ordersService';
import errorNotify from 'src/notifications/errorNotify';
import { config } from '../../config';

interface IMessage {
  senderId: number;
  message: string;
  timestamp: Date;
}

export const useWebSocket = (socketId: string, telegramId: number) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [orderData, setOrderData] = useState<IOrder | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!socketId) return;

    const newSocket = io(config.p2pAPI, { path: '/socket.io/'});

    newSocket.emit('JOIN_THE_DEAL', { socketId, telegramId });

    newSocket.on('JOINED', ({ order }) => {
      console.log('Order joined:', order);
      setOrderData(order);
      setIsConnected(true);
      newSocket.emit('FETCH_CHAT_HISTORY', { socketId });
    });

    newSocket.on('ORDER_UPDATED', (updatedOrder: IOrder) => {
      console.log('Order updated:', updatedOrder);
      setOrderData(updatedOrder);
    });

    newSocket.on('CHAT_HISTORY', (history: IMessage[]) => {
      console.log('Chat history:', history);
      setMessages(history || []);
    });

    newSocket.on('NEW_MESSAGE', (message: IMessage) => {
      console.log('New message:', message);
      setMessages((prev) => [...prev, message]);
    });

    newSocket.on('ERROR', (err) => {
      console.error('Socket error:', err);
      setIsConnected(false);
      errorNotify('Помилка підключення');
    });

    setSocket(newSocket);

    return () => {
      newSocket.off('JOINED');
      newSocket.off('ORDER_UPDATED');
      newSocket.off('CHAT_HISTORY');
      newSocket.off('NEW_MESSAGE');
      newSocket.off('ERROR');
      newSocket.disconnect();
    };
  }, [socketId, telegramId]);

  const sendMessage = (message: string) => {
    if (!socket || !socketId || !message.trim()) return;

    socket.emit('SEND_MESSAGE', { socketId, senderId: telegramId, message });
  };

  // Функція для підтвердження оплати покупцем
  const handleSendCoin = () => {
    if (!socket || !socketId || !orderData) return;

    socket.emit('UPDATE_ORDER', { socketId, statusOrder: 'PaidBuyer' });

    dispatch(updateOrderStatus(socketId, 'PaidBuyer'));

    setOrderData((prevData) => prevData ? { ...prevData, statusOrder: 'PaidBuyer' } : prevData);

    doneNotify('Оплата успішна');
  };

  // Функція для підтвердження продавцем
  const handleApprove = () => {
    if (!socket || !socketId || !orderData) return;

    socket.emit('UPDATE_ORDER', { socketId, statusOrder: 'Done' });

    dispatch(updateOrderStatus(socketId, 'Done'));

    setOrderData((prevData) => prevData ? { ...prevData, statusOrder: 'Done' } : prevData);

    doneNotify('Ордер виконано');
  };

  return {
    orderData,
    isConnected,
    messages,
    sendMessage,
    handleSendCoin,
    handleApprove,
  };
};
